import React, {useEffect, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import '../../../../styles/CorrespondenceBell.css'
import {useCorrespondenceChat} from '../context/CorrespondenceChatContext'
import {
  useGetUnreadCountQuery,
  useSearchThreadsQuery,
  useGetThreadByIdQuery,
  useAddMessageMutation,
  useMarkThreadAsReadMutation,
  useArchiveThreadMutation,
  useRestoreThreadMutation,
  useStartThreadMutation,
} from '../CorrespondenceApiSlice'
import {useAddNewDocumentMutation} from '../../documents/DocumentsApiSlice'
import {previewDocumentInNewTab} from '../../documents/previewDocumentInNewTab'
import {useAuth} from 'react-oidc-context'
import {useCorrespondenceHost} from '../host/CorrespondenceHostContext'
import {
  CorrespondenceMessageType,
  CorrespondenceParticipantRole,
  ICreateCorrespondenceAttachment,
  ICorrespondenceThread,
} from '../interfaces'
import {markThreadAsReadOnce} from '../markThreadAsReadOnce'

const ALLOWED_TYPES = '.pdf,.docx,.xlsx,.png,.jpg,.jpeg,.gif'
const MAX_ATTACHMENTS = 5

interface PendingFile {
  file: File
}

/* ─── Financier First-Time Compose ──────────────────────────────── */
const SliderComposeFirst: React.FC<{onSuccess: (threadId: string) => void}> = ({onSuccess}) => {
  const host = useCorrespondenceHost()
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [startThread, {isLoading, error}] = useStartThreadMutation()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!body.trim()) return
    const locked = host.lockedRecipients ?? []
    const threadDefaults = host.getThreadDefaults()
    try {
      const result = await startThread({
        subject: subject.trim() || undefined,
        participants: locked.map((r) => ({
          userId: r.id,
          displayName: r.displayName,
          email: r.email ?? '',
          role: r.participantRole,
        })),
        initialMessage: {
          body: body.trim(),
          messageType: host.defaultMessageType ?? CorrespondenceMessageType.External,
        },
        ...threadDefaults,
      }).unwrap()
      onSuccess(result.id)
    } catch {
      // error shown via mutation error state
    }
  }

  const analystName = host.lockedRecipients?.[0]?.displayName ?? 'your analyst'

  const inputStyle: React.CSSProperties = {
    backgroundColor: '#01364a',
    color: '#fff',
    borderColor: 'rgba(255,255,255,0.2)',
    resize: 'none',
    fontSize: '0.875rem',
  }

  return (
    <form onSubmit={handleSubmit} className='d-flex flex-column h-100 px-3 py-3 gap-3'>
      {/* Welcome message bubble */}
      <div className='d-flex gap-2 align-items-start'>
        <div
          className='d-flex align-items-center justify-content-center flex-shrink-0'
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            backgroundColor: '#009EF7',
            fontSize: '0.9rem',
            fontWeight: 700,
            color: '#fff',
          }}
        >
          {analystName.charAt(0).toUpperCase()}
        </div>
        <div
          style={{
            backgroundColor: '#01455f',
            borderRadius: '0 10px 10px 10px',
            padding: '10px 12px',
            fontSize: '0.85rem',
            color: '#fff',
            lineHeight: 1.5,
            flex: 1,
          }}
        >
          <div className='fw-semibold mb-1' style={{color: '#009EF7'}}>
            {analystName}
          </div>
          Hi! I'm {analystName}, your investment analyst at Black. How can I assist you today?
        </div>
      </div>

      {error && <div className='text-danger small'>Failed to send. Please try again.</div>}
      <div>
        <label className='text-white-50 small mb-1 d-block'>Subject (optional)</label>
        <input
          className='form-control form-control-sm'
          style={inputStyle}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder='What is this about?'
        />
      </div>
      <div className='flex-grow-1 d-flex flex-column'>
        <label className='text-white-50 small mb-1 d-block'>Message *</label>
        <textarea
          className='form-control flex-grow-1'
          style={{...inputStyle, minHeight: 120}}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder='Type your message...'
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSubmit(e as any)
          }}
        />
        <div className='text-white-50 mt-1' style={{fontSize: '0.68rem'}}>
          Ctrl+Enter to send
        </div>
      </div>
      <button type='submit' className='btn btn-primary w-100' disabled={isLoading || !body.trim()}>
        {isLoading ? (
          <span className='spinner-border spinner-border-sm me-2' />
        ) : (
          <i className='fas fa-paper-plane me-2' />
        )}
        Send Message
      </button>
    </form>
  )
}

/* ─── Helpers ───────────────────────────────────────────────────── */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function fileIcon(fileType: string): string {
  if (['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'].includes(fileType))
    return 'fa-file-image'
  if (fileType.includes('pdf')) return 'fa-file-pdf'
  if (fileType.includes('word') || fileType.includes('docx')) return 'fa-file-word'
  if (fileType.includes('sheet') || fileType.includes('xlsx') || fileType.includes('excel'))
    return 'fa-file-excel'
  return 'fa-file-alt'
}

const AVATAR_COLORS = ['#0f6b8e', '#1a7a5e', '#6b4e9e', '#8e5e0f', '#0f5e6b', '#7a1a3a']
function getAvatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function roleLabel(role: CorrespondenceParticipantRole): string {
  switch (role) {
    case CorrespondenceParticipantRole.Financier:
      return 'Financier'
    case CorrespondenceParticipantRole.Analyst:
      return 'Investment Analyst'
    case CorrespondenceParticipantRole.Manager:
      return 'Manager'
    default:
      return 'Admin'
  }
}

/* ─── Inline Thread Detail ─────────────────────────────────────── */
const SliderThreadDetail: React.FC<{
  threadId: string
  onBack: () => void
  onFullView: () => void
  hideMessageType: boolean
  defaultMessageType: CorrespondenceMessageType
}> = ({threadId, onBack, onFullView, hideMessageType, defaultMessageType}) => {
  const auth = useAuth()
  const currentUserId = auth.user?.profile?.sub
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [body, setBody] = useState('')
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [msgType, setMsgType] = useState<CorrespondenceMessageType>(defaultMessageType)

  const {data: thread, isLoading} = useGetThreadByIdQuery(threadId)
  const [addMessage, {isLoading: isSending}] = useAddMessageMutation()
  const [addNewDocument, {isLoading: isUploadingAttachments}] = useAddNewDocumentMutation()
  const [markAsRead] = useMarkThreadAsReadMutation()

  // Build a map of userId → participant for role lookup
  const participantMap = React.useMemo(() => {
    const map = new Map<
      string,
      typeof thread extends undefined ? never : NonNullable<typeof thread>['participants'][number]
    >()
    thread?.participants?.forEach((p) => map.set(p.userId, p))
    return map
  }, [thread?.participants])

  const analystParticipant = thread?.participants?.find(
    (p) => p.role === CorrespondenceParticipantRole.Analyst
  )

  // If the thread involves a financier, default replies to External
  useEffect(() => {
    if (!thread) return
    const hasFinancier = thread.participants?.some(
      (p) => p.role === CorrespondenceParticipantRole.Financier
    )
    if (hasFinancier) setMsgType(CorrespondenceMessageType.External)
  }, [thread?.id]) // eslint-disable-line

  useEffect(() => {
    if (!thread) return

    const hasUnread = thread.messages?.some(
      (message) => !message.isReadByCurrentUser && message.authorId !== currentUserId
    )

    markThreadAsReadOnce(threadId, Boolean(hasUnread), markAsRead)
  }, [thread?.id]) // eslint-disable-line

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'})
  }, [thread?.messages?.length])

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!body.trim() && pendingFiles.length === 0) return

    let attachments: ICreateCorrespondenceAttachment[] = []

    if (pendingFiles.length > 0) {
      const filesToUpload = await Promise.all(
        pendingFiles.map(
          (pf) =>
            new Promise<{
              fileName: string
              fileType: string
              fileContent: string
              fileSize: number
              dateUploaded: string
            }>((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => {
                const dataUrl = reader.result as string
                const base64 = dataUrl.split(',')[1]
                resolve({
                  fileName: pf.file.name,
                  fileType: pf.file.type,
                  fileContent: base64,
                  fileSize: pf.file.size,
                  dateUploaded: new Date().toISOString(),
                })
              }
              reader.onerror = reject
              reader.readAsDataURL(pf.file)
            })
        )
      )

      const uploadResult = await addNewDocument({files: filesToUpload}).unwrap()
      const uploaded: {id: number; fileName: string; fileType: string; fileSize: number}[] =
        Array.isArray(uploadResult) ? uploadResult : []
      attachments = uploaded.map((doc, i) => ({
        documentAssetId: String(doc.id ?? ''),
        fileName: doc.fileName ?? filesToUpload[i]?.fileName ?? '',
        fileType: doc.fileType ?? filesToUpload[i]?.fileType ?? '',
        fileSize: doc.fileSize ?? filesToUpload[i]?.fileSize ?? 0,
      }))
    }

    await addMessage({
      threadId,
      body: {body: body.trim(), messageType: msgType, attachments},
    }).unwrap()
    setBody('')
    setPendingFiles([])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    const combined = [...pendingFiles, ...files.map((f) => ({file: f}))].slice(0, MAX_ATTACHMENTS)
    setPendingFiles(combined)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index))
  }

  return (
    <div className='d-flex flex-column h-100'>
      {/* Header */}
      <div
        className='d-flex align-items-center gap-2 px-3 py-2 flex-shrink-0'
        style={{borderBottom: '1px solid rgba(255,255,255,0.1)'}}
      >
        <button
          className='btn btn-sm p-0 me-1'
          style={{color: 'rgba(255,255,255,0.6)', background: 'none', border: 'none'}}
          onClick={onBack}
          title='Back'
        >
          <i className='fas fa-arrow-left' />
        </button>
        <span className='fw-semibold text-white flex-grow-1 text-truncate'>
          {isLoading ? '...' : thread?.subject}
        </span>
        <button
          className='btn btn-sm p-0'
          style={{color: 'rgba(255,255,255,0.6)', background: 'none', border: 'none'}}
          onClick={onFullView}
          title='Full view'
        >
          <i className='fas fa-expand-alt' />
        </button>
      </div>

      {/* Messages */}
      <div className='flex-grow-1 overflow-auto px-3 py-2' style={{minHeight: 0}}>
        {isLoading && (
          <div className='text-center py-4'>
            <div className='spinner-border spinner-border-sm text-light' />
          </div>
        )}
        {/* Static welcome bubble from analyst */}
        {analystParticipant && (
          <div className='mb-3 d-flex gap-2 flex-row'>
            {/* Avatar */}
            <div
              style={{
                flexShrink: 0,
                width: 30,
                height: 30,
                borderRadius: '50%',
                backgroundColor: getAvatarColor(analystParticipant.displayName),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.65rem',
                fontWeight: 600,
                color: '#fff',
                alignSelf: 'flex-start',
                marginTop: 2,
              }}
              aria-hidden='true'
            >
              {getInitials(analystParticipant.displayName)}
            </div>
            <div className='d-flex flex-column align-items-start' style={{maxWidth: '78%'}}>
              <div className='mb-1'>
                <span className='fw-semibold text-white' style={{fontSize: '0.78rem'}}>
                  {analystParticipant.displayName}
                </span>
                <div style={{fontSize: '0.68rem', color: '#009EF7', marginTop: 1}}>
                  {roleLabel(analystParticipant.role)}
                </div>
              </div>
              <div
                className='rounded px-3 py-2'
                style={{
                  backgroundColor: 'rgba(255,255,255,0.07)',
                  color: '#fff',
                  maxWidth: '100%',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  borderLeft: '2px solid #009EF7',
                }}
              >
                Hi! I'm {analystParticipant.displayName}, your investment analyst at Black. How can
                I assist you today?
              </div>
            </div>
          </div>
        )}

        {!isLoading && (!thread?.messages || thread.messages.length === 0) && (
          <div className='text-white-50 text-center small py-3'>Send a message to get started.</div>
        )}
        {thread?.messages?.map((msg) => {
          const isMine = msg.authorId === currentUserId
          const participant = participantMap.get(msg.authorId)
          return (
            <div
              key={msg.id}
              className={`mb-3 d-flex gap-2 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}
            >
              {/* Avatar */}
              <div
                style={{
                  flexShrink: 0,
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  backgroundColor: getAvatarColor(msg.authorName),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  color: '#fff',
                  alignSelf: 'flex-start',
                  marginTop: 2,
                }}
                aria-hidden='true'
              >
                {getInitials(msg.authorName)}
              </div>

              <div
                className={`d-flex flex-column ${isMine ? 'align-items-end' : 'align-items-start'}`}
                style={{maxWidth: '78%'}}
              >
                {/* Meta */}
                <div className='mb-1' style={{textAlign: isMine ? 'right' : 'left'}}>
                  <span className='fw-semibold text-white' style={{fontSize: '0.78rem'}}>
                    {msg.authorName}
                  </span>
                  {participant && (
                    <div style={{fontSize: '0.68rem', color: '#009EF7', marginTop: 1}}>
                      {roleLabel(participant.role)}
                    </div>
                  )}
                  <div style={{fontSize: '0.68rem', color: 'rgba(255,255,255,0.38)', marginTop: 1}}>
                    {new Date(msg.dateCreated).toLocaleString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
                {/* Bubble */}
                <div
                  className='rounded px-3 py-2'
                  style={{
                    backgroundColor: isMine ? '#024d6e' : 'rgba(255,255,255,0.07)',
                    color: '#fff',
                    maxWidth: '100%',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {msg.body}

                  {/* Attachments */}
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div
                      className='d-flex flex-column gap-1'
                      style={{
                        marginTop: '0.5rem',
                        paddingTop: '0.4rem',
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      {msg.attachments.map((att: any) => (
                        <button
                          key={att.id}
                          type='button'
                          aria-label={`Preview ${att.fileName}`}
                          onClick={() => void previewDocumentInNewTab(att.documentAssetId)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            width: '100%',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.3rem',
                            backgroundColor: 'rgba(0,0,0,0.25)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: '#fff',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: '0.78rem',
                          }}
                        >
                          <i
                            className={`fas ${fileIcon(att.fileType)}`}
                            style={{color: '#009EF7', fontSize: '0.8rem', flexShrink: 0}}
                            aria-hidden='true'
                          />
                          <span
                            style={{
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {att.fileName}
                          </span>
                          <span
                            style={{
                              color: 'rgba(255,255,255,0.4)',
                              flexShrink: 0,
                              fontSize: '0.68rem',
                            }}
                          >
                            {formatFileSize(att.fileSize)}
                          </span>
                          <i
                            className='fas fa-external-link-alt'
                            style={{
                              fontSize: '0.62rem',
                              color: 'rgba(255,255,255,0.35)',
                              flexShrink: 0,
                            }}
                            aria-hidden='true'
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply */}
      <form
        onSubmit={handleSend}
        className='flex-shrink-0 px-3 py-2'
        style={{borderTop: '1px solid rgba(255,255,255,0.1)'}}
      >
        {!hideMessageType && (
          <div
            className='d-flex align-items-center gap-1 mb-2'
            role='radiogroup'
            aria-label='Message visibility'
          >
            {[
              {val: CorrespondenceMessageType.Internal, label: 'Internal'},
              {val: CorrespondenceMessageType.External, label: 'External'},
            ].map(({val, label}) => {
              const active = msgType === val
              return (
                <label
                  key={val}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '2rem',
                    cursor: 'pointer',
                    fontSize: '0.78rem',
                    fontWeight: active ? 600 : 400,
                    backgroundColor: active ? 'rgba(0,158,247,0.15)' : 'transparent',
                    border: active ? '1px solid rgba(0,158,247,0.35)' : '1px solid transparent',
                    color: active ? '#009EF7' : 'rgba(255,255,255,0.4)',
                    transition: 'all 0.15s ease',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type='radio'
                    name='sliderMsgType'
                    style={{display: 'none'}}
                    checked={active}
                    onChange={() => setMsgType(val)}
                  />
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      backgroundColor: active ? '#009EF7' : 'rgba(255,255,255,0.25)',
                      display: 'inline-block',
                      flexShrink: 0,
                    }}
                    aria-hidden='true'
                  />
                  {label}
                </label>
              )
            })}
          </div>
        )}

        {/* Composer card — matches ThreadDetailPage design */}
        <div
          style={{
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '0.6rem',
            backgroundColor: 'rgba(1,54,74,0.55)',
            overflow: 'hidden',
          }}
        >
          {/* Pending attachment chips */}
          {pendingFiles.length > 0 && (
            <div className='d-flex flex-wrap gap-2 px-3 pt-2 pb-1'>
              {pendingFiles.map((pf, i) => (
                <div
                  key={i}
                  role='listitem'
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    backgroundColor: 'rgba(0,158,247,0.08)',
                    border: '1px solid rgba(0,158,247,0.25)',
                    borderRadius: '0.375rem',
                    padding: '0.22rem 0.6rem',
                    fontSize: '0.78rem',
                    color: 'rgba(255,255,255,0.8)',
                  }}
                >
                  <i
                    className={`fas ${fileIcon(pf.file.type)}`}
                    style={{color: '#009EF7', fontSize: '0.72rem', flexShrink: 0}}
                    aria-hidden='true'
                  />
                  <span
                    style={{
                      maxWidth: 130,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {pf.file.name}
                  </span>
                  <span
                    style={{color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem', flexShrink: 0}}
                  >
                    {formatFileSize(pf.file.size)}
                  </span>
                  <button
                    type='button'
                    className='border-0 bg-transparent p-0 lh-1'
                    style={{color: 'rgba(255,255,255,0.4)', cursor: 'pointer', marginLeft: 2}}
                    onClick={() => removePendingFile(i)}
                    aria-label={`Remove ${pf.file.name}`}
                  >
                    <i className='fas fa-times' style={{fontSize: '0.65rem'}} aria-hidden='true' />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Textarea */}
          <textarea
            className='form-control border-0 bg-transparent px-3'
            rows={3}
            autoFocus
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder='Write a reply…'
            aria-label='Reply message'
            style={{
              color: '#fff',
              resize: 'none',
              outline: 'none',
              boxShadow: 'none',
              fontSize: '0.9rem',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend(e as any)
            }}
          />

          {/* Composer toolbar */}
          <div
            className='d-flex align-items-center justify-content-between px-3 py-2'
            style={{borderTop: '1px solid rgba(255,255,255,0.07)'}}
          >
            <div className='d-flex align-items-center gap-2'>
              <input
                ref={fileInputRef}
                type='file'
                accept={ALLOWED_TYPES}
                multiple
                style={{display: 'none'}}
                aria-hidden='true'
                onChange={handleFileChange}
              />
              <button
                type='button'
                title={
                  pendingFiles.length >= MAX_ATTACHMENTS
                    ? `Max ${MAX_ATTACHMENTS} files reached`
                    : 'Attach files'
                }
                disabled={
                  pendingFiles.length >= MAX_ATTACHMENTS || isUploadingAttachments || isSending
                }
                onClick={() => fileInputRef.current?.click()}
                aria-label='Attach files'
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  padding: '0.35rem 0.65rem',
                  borderRadius: '0.375rem',
                  border: '1px solid rgba(255,255,255,0.12)',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  color:
                    pendingFiles.length >= MAX_ATTACHMENTS
                      ? 'rgba(255,255,255,0.2)'
                      : 'rgba(255,255,255,0.55)',
                  cursor: pendingFiles.length >= MAX_ATTACHMENTS ? 'not-allowed' : 'pointer',
                  fontSize: '0.8rem',
                  transition: 'background-color 0.12s ease',
                }}
              >
                <i className='fas fa-paperclip' aria-hidden='true' />
                {pendingFiles.length > 0 ? (
                  <span style={{color: '#009EF7', fontWeight: 600, fontSize: '0.75rem'}}>
                    {pendingFiles.length}/{MAX_ATTACHMENTS}
                  </span>
                ) : (
                  <span style={{fontSize: '0.75rem'}}>Attach files</span>
                )}
              </button>
              <span style={{fontSize: '0.7rem', color: 'rgba(255,255,255,0.25)'}}>
                Ctrl+Enter to send
              </span>
            </div>

            <button
              type='submit'
              className='btn btn-primary btn-sm d-flex align-items-center gap-2'
              disabled={
                isSending || isUploadingAttachments || (!body.trim() && pendingFiles.length === 0)
              }
              style={{minWidth: 80}}
              aria-label='Send reply'
            >
              {isSending || isUploadingAttachments ? (
                <>
                  <span
                    className='spinner-border spinner-border-sm'
                    role='status'
                    aria-hidden='true'
                  />
                  <span>Sending…</span>
                </>
              ) : (
                <>
                  <i
                    className='fas fa-paper-plane'
                    style={{fontSize: '0.78rem'}}
                    aria-hidden='true'
                  />
                  Send
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

/* ─── Thread Row ────────────────────────────────────────────────── */
const ThreadRow: React.FC<{
  thread: ICorrespondenceThread
  tab: 'inbox' | 'archived'
  onClick: () => void
  onArchive: () => void
  onRestore: () => void
}> = ({thread, tab, onClick, onArchive, onRestore}) => {
  const [btnHovered, setBtnHovered] = useState(false)
  return (
    <div
      className='w-100 text-start d-flex flex-column px-3 py-2'
      style={{
        background: 'none',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        color: thread.hasUnread ? '#fff' : 'rgba(255,255,255,0.75)',
        cursor: 'pointer',
        fontWeight: thread.hasUnread ? 600 : 400,
      }}
      onClick={onClick}
    >
      <div className='d-flex align-items-start justify-content-between w-100 gap-2'>
        <span
          className='text-truncate flex-grow-1'
          style={{fontSize: '0.875rem', fontWeight: thread.hasUnread ? 700 : 500}}
        >
          {thread.subject}
        </span>
        <button
          className='btn btn-sm p-0 flex-shrink-0'
          style={{
            background: 'none',
            border: 'none',
            fontSize: '0.7rem',
            lineHeight: 1,
            color: btnHovered ? '#fff' : 'rgba(255,255,255,0.4)',
            transition: 'color 0.15s',
          }}
          title={tab === 'inbox' ? 'Archive' : 'Restore'}
          onMouseEnter={() => setBtnHovered(true)}
          onMouseLeave={() => setBtnHovered(false)}
          onClick={(e) => {
            e.stopPropagation()
            tab === 'inbox' ? onArchive() : onRestore()
          }}
        >
          <i
            className={tab === 'inbox' ? 'fas fa-archive' : 'fas fa-inbox'}
            style={{pointerEvents: 'none'}}
          />
        </button>
      </div>
      <span className='small' style={{color: thread.hasUnread ? '#fff' : 'rgba(255,255,255,0.5)'}}>
        {thread.lastMessageAuthorName ??
          thread.participants
            .filter((p) => p.isActive && !p.isDeleted)
            .map((p) => p.displayName)
            .join(', ')}
      </span>
      {thread.lastMessageBody && (
        <span
          className='mt-1'
          style={{
            fontSize: '0.78rem',
            color: thread.hasUnread ? '#fff' : 'rgba(255,255,255,0.5)',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {thread.lastMessageBody}
        </span>
      )}
    </div>
  )
}

/* ─── Thread List ───────────────────────────────────────────────── */
const SliderThreadList: React.FC<{
  financierId?: string
  onThreadClick: (thread: ICorrespondenceThread) => void
  onFullView: () => void
  onNewChat?: () => void
  currentUserId?: string
}> = ({financierId, onThreadClick, onFullView, onNewChat, currentUserId}) => {
  const auth = useAuth()
  const [tab, setTab] = useState<'inbox' | 'archived'>('inbox')
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 20
  const [archiveThread] = useArchiveThreadMutation()
  const [restoreThread] = useRestoreThreadMutation()

  const {data, isLoading, isFetching} = useSearchThreadsQuery(
    {
      page,
      pageSize: PAGE_SIZE,
      isArchived: tab === 'archived',
      ...(financierId ? {financierId} : {}),
    },
    {skip: !auth.isAuthenticated, refetchOnMountOrArgChange: true}
  )

  const threads = data?.data ?? []
  const totalPages = data?.paginationMetadata?.totalPages ?? 1

  return (
    <div className='d-flex flex-column h-100'>
      {/* Header */}
      <div
        className='d-flex align-items-center justify-content-between px-3 py-2 flex-shrink-0'
        style={{borderBottom: '1px solid rgba(255,255,255,0.1)'}}
      >
        <span className='fw-bold text-white'>Messages</span>
        <div className='d-flex align-items-center gap-2'>
          <button
            className='btn btn-sm p-0'
            style={{
              color: 'rgba(255,255,255,0.6)',
              background: 'none',
              border: 'none',
              fontSize: '0.75rem',
            }}
            onClick={onFullView}
            title='Open full inbox'
          >
            <i className='fas fa-expand-alt me-1' />
            Full View
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div
        className='d-flex flex-shrink-0'
        style={{borderBottom: '1px solid rgba(255,255,255,0.1)'}}
      >
        {(['inbox', 'archived'] as const).map((t) => (
          <button
            key={t}
            className='btn btn-sm flex-grow-1'
            style={{
              borderRadius: 0,
              color: tab === t ? '#fff' : 'rgba(255,255,255,0.45)',
              borderBottom: tab === t ? '2px solid #009EF7' : '2px solid transparent',
              background: 'none',
              fontSize: '0.8rem',
              padding: '6px 0',
            }}
            onClick={() => {
              setTab(t)
              setPage(1)
            }}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* List */}
      <div className='flex-grow-1 overflow-auto' style={{minHeight: 0}}>
        {(isLoading || isFetching) && (
          <div className='text-center py-4'>
            <div className='spinner-border spinner-border-sm text-light' />
          </div>
        )}
        {!isLoading && !isFetching && threads.length === 0 && (
          <div className='text-white-50 text-center small py-4'>
            {tab === 'inbox' ? 'No messages in your inbox.' : 'No archived messages.'}
          </div>
        )}
        {threads.map((thread) => (
          <ThreadRow
            key={thread.id}
            thread={thread}
            tab={tab}
            onClick={() => onThreadClick(thread)}
            onArchive={() => archiveThread(thread.id)}
            onRestore={() => restoreThread(thread.id)}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          className='d-flex justify-content-between align-items-center px-3 py-2 flex-shrink-0'
          style={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.5)',
          }}
        >
          <button
            className='btn btn-sm p-0'
            style={{color: 'rgba(255,255,255,0.6)', background: 'none', border: 'none'}}
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <i className='fas fa-chevron-left' />
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            className='btn btn-sm p-0'
            style={{color: 'rgba(255,255,255,0.6)', background: 'none', border: 'none'}}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            <i className='fas fa-chevron-right' />
          </button>
        </div>
      )}

      {/* Pinned New Chat button */}
      {onNewChat && (
        <div
          className='flex-shrink-0 px-3 py-2'
          style={{borderTop: '1px solid rgba(255,255,255,0.1)'}}
        >
          <button className='btn btn-primary w-100 btn-sm' onClick={onNewChat}>
            <i className='fas fa-pen me-2' />
            New Chat
          </button>
        </div>
      )}
    </div>
  )
}

/* ─── Bell (Notifications Only) ────────────────────────────────── */
const CorrespondenceBell: React.FC = () => {
  const auth = useAuth()
  const {open: openChat} = useCorrespondenceChat()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const {data: unreadData} = useGetUnreadCountQuery(undefined, {skip: !auth.isAuthenticated})
  const unreadCount = unreadData?.total ?? 0

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    if (dropdownOpen) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dropdownOpen])

  return (
    <div ref={dropdownRef} style={{position: 'relative'}}>
      {/* Bell icon */}
      <span
        className='position-relative d-flex align-items-center justify-content-center'
        style={{cursor: 'pointer', width: 36, height: 36}}
        onClick={() => setDropdownOpen((p) => !p)}
        title='Notifications'
      >
        <span className='fas fa-bell fs-4 text-white' />
        {unreadCount > 0 && (
          <span
            className='position-absolute badge rounded-pill bg-danger'
            style={{top: 0, right: 0, fontSize: '0.65rem', padding: '2px 5px', minWidth: 18}}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </span>

      {/* Notification dropdown */}
      {dropdownOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 280,
            backgroundColor: '#012836',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            zIndex: 1070,
            overflow: 'hidden',
          }}
        >
          <div className='px-3 py-2' style={{borderBottom: '1px solid rgba(255,255,255,0.1)'}}>
            <span className='fw-semibold text-white small'>Notifications</span>
          </div>
          {unreadCount > 0 ? (
            <div className='px-3 py-3'>
              <div className='d-flex align-items-center gap-2 mb-3'>
                <i className='fas fa-comment-alt text-primary' />
                <span className='text-white small'>
                  You have <strong>{unreadCount}</strong> unread message
                  {unreadCount !== 1 ? 's' : ''}
                </span>
              </div>
              <button
                className='btn btn-primary btn-sm w-100'
                onClick={() => {
                  setDropdownOpen(false)
                  openChat()
                }}
              >
                Open Messages
              </button>
            </div>
          ) : (
            <div className='px-3 py-4 text-center text-white-50 small'>No new notifications</div>
          )}
        </div>
      )}
    </div>
  )
}

/* ─── Chat FAB + Slider ─────────────────────────────────────────── */
export const CorrespondenceChatFab: React.FC = () => {
  const auth = useAuth()
  const navigate = useNavigate()
  const host = useCorrespondenceHost()
  const {isOpen, open, close, activeThreadId, setActiveThreadId} = useCorrespondenceChat()
  const fabRef = useRef<HTMLDivElement>(null)

  const {financierId} = host.getThreadDefaults()
  const hideMessageType = host.hideRecipientPicker ?? false
  const defaultMsgType = host.defaultMessageType ?? CorrespondenceMessageType.Internal

  const {data: unreadData} = useGetUnreadCountQuery(undefined, {skip: !auth.isAuthenticated})
  const unreadCount = unreadData?.total ?? 0

  const {data: firstPageData} = useSearchThreadsQuery(
    {page: 1, pageSize: 1, ...(financierId ? {financierId} : {})},
    {skip: !auth.isAuthenticated || !hideMessageType, refetchOnMountOrArgChange: true}
  )
  const financierHasThreads = hideMessageType ? (firstPageData?.data?.length ?? 0) > 0 : false

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (fabRef.current && !fabRef.current.contains(e.target as Node)) {
        close()
      }
    }
    if (isOpen) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen, close])

  const handleFullInbox = () => {
    close()
    navigate(host.inboxRoute())
  }

  const handleNewChat = () => {
    close()
    navigate(host.inboxRoute(), {state: {openCompose: true}})
  }

  const handleFullThread = () => {
    if (activeThreadId) {
      close()
      navigate(host.threadRoute(activeThreadId))
    }
  }

  if (!auth.isAuthenticated) return null

  return (
    <div ref={fabRef}>
      {/* FAB button */}
      <button
        onClick={() => (isOpen ? close() : open())}
        style={{
          position: 'fixed',
          bottom: 28,
          right: 28,
          width: 52,
          height: 52,
          borderRadius: '50%',
          backgroundColor: '#009EF7',
          border: 'none',
          color: '#fff',
          fontSize: '1.2rem',
          boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
          zIndex: 1055,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s',
        }}
        title='Messages'
      >
        <i
          className={isOpen ? 'fas fa-times' : 'fas fa-comment-alt'}
          style={{fontSize: '1.5rem', color: '#fff'}}
        />
        {!isOpen && unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              fontSize: '0.6rem',
              padding: '2px 4px',
              minWidth: 16,
              backgroundColor: '#f1416c',
              color: '#fff',
              borderRadius: 9999,
              lineHeight: 1.2,
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Slide-in drawer */}
      <div
        style={{
          position: 'fixed',
          top: 54,
          right: 0,
          width: 380,
          height: 'calc(100vh - 54px)',
          backgroundColor: '#012836',
          borderLeft: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.5)',
          zIndex: 1060,
          display: 'flex',
          flexDirection: 'column',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.25s ease',
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
      >
        {activeThreadId ? (
          <SliderThreadDetail
            threadId={activeThreadId}
            onBack={() => setActiveThreadId(null)}
            onFullView={handleFullThread}
            hideMessageType={hideMessageType}
            defaultMessageType={defaultMsgType}
          />
        ) : hideMessageType && host.canStartThread && !financierHasThreads ? (
          <SliderComposeFirst onSuccess={(id) => setActiveThreadId(id)} />
        ) : (
          <SliderThreadList
            financierId={financierId}
            onThreadClick={(t) => setActiveThreadId(t.id)}
            onFullView={handleFullInbox}
            onNewChat={!hideMessageType ? handleNewChat : undefined}
            currentUserId={auth.user?.profile?.sub}
          />
        )}
      </div>
    </div>
  )
}

export default CorrespondenceBell
